import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DahshboardOverview, RevenueDailyTrend } from "../services/dashboard.services";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const Dashboard = () => {
  const [data, setData] = useState({});
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendRes] = await Promise.all([
        DahshboardOverview(period),
        RevenueDailyTrend(period),
      ]);

      if (overviewRes?.data) {
        setData(overviewRes.data);
      }

      if (trendRes?.data?.labels && Array.isArray(trendRes.data.labels)) {
        const formatted = trendRes.data.labels.map((label, index) => ({
          label, // e.g., "02 Dec"
          // Consultations
          consultation_revenue: trendRes.data.consultations?.revenue?.[index] ?? 0,
          consultation_count: trendRes.data.consultations?.count?.[index] ?? 0,
          // Remedies
          remedies_revenue: trendRes.data.remedies?.revenue?.[index] ?? 0,
          remedies_count: trendRes.data.remedies?.count?.[index] ?? 0,
          // Puja
          puja_revenue: trendRes.data.puja?.revenue?.[index] ?? 0,
          puja_count: trendRes.data.puja?.count?.[index] ?? 0,
        }));
        setTrend(formatted);
      } else {
        setTrend([]);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setTrend([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  const {
    users = 0,
    astrologers = 0,
    bookings = 0,
    pendingWithdrawals: pendingCount = 0,
    recentWithdrawals = [],
    topAstrologers = [],
  } = data;

  return (
    <div className="container-fluid py-4">
      <h2 className="mb-4 fw-bold">Dashboard Overview</h2>

      {/* Period Selector */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <select
          className="form-select w-auto"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div className="row g-4 mb-5">
        {[
          { title: "Users", value: users, icon: "👥", color: "primary", path: "/admin/user-management/user/list/1" },
          { title: "Astrologers", value: astrologers, icon: "🔮", color: "info", path: "/admin/astrologer-management/astrologer/list/1" },
          { title: "Consultations Booking", value: bookings, icon: "📅", color: "success", path: "/admin/consultation/list/1" },
          { title: "Pending Withdrawals", value: pendingCount, icon: "⏳", color: "warning", path: "/admin/withdrawal/list/1" },
        ].map((kpi, i) => (
          <div key={i} className="col-md-3">
            <div
              className={`card border-0 shadow-sm text-white bg-${kpi.color} h-100`}
              role="button"
              style={{ cursor: "pointer", transition: "transform 0.2s" }}
              onClick={() => navigate(kpi.path)}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-5px)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
            >
              <div className="card-body text-center py-4">
                <div className="fs-1 mb-3">{kpi.icon}</div>
                <h5 className="mb-2">{kpi.title}</h5>
                <h3 className="mb-0">{kpi.value.toLocaleString()}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue & Orders Trend Chart */}
      <div className="card border-0 shadow-sm mb-5">
        <div className="card-header bg-light">
          <h5 className="mb-0">
            Revenue & Orders Trend ({period === "day" ? "Today" : period === "week" ? "This Week" : "This Month"})
          </h5>
        </div>
        <div className="card-body">
          {trend.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No data available for selected period</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={trend} margin={{ top: 20, right: 40, left: 40, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                {/* X-Axis: Dates */}
                <XAxis
                  dataKey="label"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />

                {/* Left Y-Axis: Revenue in ₹ */}
                <YAxis
                  yAxisId="revenue"
                  orientation="left"
                  tickFormatter={(value) => `₹${value.toLocaleString()}`}
                  label={{ value: "Revenue (₹)", angle: -90, position: "insideLeft" }}
                />

                {/* Right Y-Axis: Order Count */}
                <YAxis
                  yAxisId="count"
                  orientation="right"
                  label={{ value: "Orders Count", angle: 90, position: "insideRight" }}
                />

                {/* Tooltip */}
                <Tooltip
                  formatter={(value, name) => {
                    const displayName = name
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase());
                    return name.includes("revenue")
                      ? `₹${Number(value).toLocaleString()}`
                      : value;
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                  labelStyle={{ fontWeight: "bold", color: "#333" }}
                />

                {/* Legend */}
                <Legend
                  verticalAlign="top"
                  height={60}
                  iconType="line"
                  wrapperStyle={{ paddingTop: "10px" }}
                />

                {/* Revenue Lines (Left Axis) */}
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="consultation_revenue"
                  stroke="#28a745" // Green
                  strokeWidth={3}
                  name="Consultation Revenue"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="remedies_revenue"
                  stroke="#ffc107" // Amber
                  strokeWidth={3}
                  name="Remedies Revenue"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="puja_revenue"
                  stroke="#dc3545" // Red
                  strokeWidth={3}
                  name="Puja Revenue"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />

                {/* Count Lines (Right Axis) */}
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="consultation_count"
                  stroke="#17a2b8" // Cyan
                  strokeWidth={3}
                  name="Consultation Orders"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="remedies_count"
                  stroke="#6f42c1" // Purple
                  strokeWidth={3}
                  name="Remedies Orders"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="puja_count"
                  stroke="#fd7e14" // Orange
                  strokeWidth={3}
                  name="Puja Orders"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Bottom Sections: Pending Withdrawals & Top Astrologers */}
      <div className="row g-4">
        {/* Pending Withdrawals */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Pending Withdrawals ({pendingCount})</h5>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Astrologer</th>
                    <th>Amount</th>
                    <th>Date</th>
                    {/* <th>Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {recentWithdrawals.length > 0 ? (
                    recentWithdrawals.map((w) => (
                      <tr key={w._id}>
                        <td>{w.astrologer?.name || "N/A"}</td>
                        <td className="fw-bold">₹{w.amount}</td>
                        <td>{moment(w.createdAt).format("DD/MM/YYYY")}</td>
                        {/* <td>
                          <button className="btn btn-sm btn-success me-2">Approve</button>
                          <button className="btn btn-sm btn-danger">Reject</button>
                        </td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No pending withdrawals
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Top Astrologers */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Top Performing Astrologers</h5>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Earnings</th>
                    <th>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {topAstrologers.length > 0 ? (
                    topAstrologers.map((a, i) => (
                      <tr key={a._id || i}>
                        <td>{i + 1}</td>
                        <td>{a.name}</td>
                        <td className="fw-bold">₹{a.earnings?.toLocaleString()}</td>
                        <td>{a.sessions}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;